import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Paper,
  Grid,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  AppBar,
  Toolbar,
  Container,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Divider
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { tokens } from "../../theme";
import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/auth';
import './ContactEdit.css';

const ContactCreate = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // Loading and error states
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state with all Unomi properties
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    countryName: '',
    state: '',
    jobTitle: '',
    nationality: '',
    birthDate: '', // YYYY/MM/DD format
    maritalStatus: '',
    gender: '',
    profession: '',
    language: '',
    levelOfEducation: '',
    levelOfSatisfaction: '',
    formOfCommunication: '',
    facebookId: '',
    kids: '',
    interests: '',
    subscriptionStatus: '',
    customerType: '',
    preferredChannel: ''
  });

  const [age, setAge] = useState(null);

  // Utility function to convert date to Unix timestamp
  const dateToUnixTimestamp = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return Math.floor(date.getTime());
  };

  // Calculate age from birthDate (for display only)
  const calculateAge = (birthTimestamp) => {
    if (!birthTimestamp) return null;
    const today = new Date();
    const birthDate = new Date(birthTimestamp);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    
    setFormData({
      ...formData,
      [field]: value
    });
    
    // If birthDate is changed, update age calculation
    if (field === 'birthDate' && value) {
      const timestamp = dateToUnixTimestamp(value);
      if (timestamp) {
        setAge(calculateAge(timestamp));
      }
    }
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '?';
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      // Validate required fields on frontend
      if (!formData.email || !formData.email.trim()) {
        setError('L\'adresse e-mail est requise');
        return;
      }
      
      if (!formData.firstName || !formData.firstName.trim()) {
        setError('Le prénom est requis');
        return;
      }
      
      if (!formData.lastName || !formData.lastName.trim()) {
        setError('Le nom de famille est requis');
        return;
      }
      
      // Prepare the properties object
      const properties = {};
      
      // Add all form data to properties
      Object.keys(formData).forEach(key => {
        if (formData[key] && formData[key].trim && formData[key].trim() !== '') {
          properties[key] = formData[key];
        } else if (formData[key] && typeof formData[key] === 'string' && formData[key] !== '') {
          properties[key] = formData[key];
        }
      });
      
      // Handle birthDate conversion to Unix timestamp
      if (formData.birthDate) {
        properties.birthDate = dateToUnixTimestamp(formData.birthDate);
      }
      
      // Create the profile payload (without itemId - backend will generate it)
      const createPayload = {
        itemType: "profile",
        properties: properties
      };
      
      console.log('Creating profile with payload:', createPayload);
      
      // Call the create API
      const response = await api.post('/profiles', createPayload);
      const createdProfile = response.data.profile;
      
      console.log('Profile created successfully:', createdProfile);
      
      // Navigate to the newly created profile's detail page
      navigate(`/contacts/${createdProfile.itemId}`);
      
    } catch (err) {
      console.error('Error creating profile:', err);
      setError(err.response?.data?.detail || 'Échec de la création du profil');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/contacts');
  };

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid #dee2e6', mb: 3 }}>
        <Container maxWidth="xl">
          <Toolbar sx={{ px: 0 }}>
            <IconButton 
              onClick={() => navigate('/contacts')}
              sx={{ mr: 2, color: '#6c757d' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={600} color="#1e293b">
              Créer un nouveau contact
            </Typography>
          </Toolbar>
        </Container>
      </Paper>

      {/* Toast Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Profil créé avec succès !
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ pb: 10 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Main Form Section - Full Width (No System Info Sidebar) */}
            <Grid item xs={12}>
              {/* Personal Information Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  mb: 3, 
                  borderRadius: 2,
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.06)'
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#1e293b' }}>
                  Informations personnelles
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                  {/* Avatar Display (No Upload) */}
                  <Box sx={{ flexShrink: 0 }}>
                    <Avatar
                      sx={{
                        width: 96,
                        height: 96,
                        fontSize: '32px',
                        fontWeight: 300,
                        backgroundColor: '#e2e8f0',
                        color: '#64748b'
                      }}
                    >
                      {getInitials(formData.firstName, formData.lastName)}
                    </Avatar>
                  </Box>

                  {/* Form Fields */}
                  <Box sx={{ flex: 1 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Prénom <span style={{ color: '#dc3545' }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange('firstName')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Nom de famille <span style={{ color: '#dc3545' }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange('lastName')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Adresse e-mail <span style={{ color: '#dc3545' }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange('email')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Numéro de téléphone
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="tel"
                          placeholder="N/A"
                          value={formData.phoneNumber}
                          onChange={handleInputChange('phoneNumber')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Date de naissance (AAAA/MM/JJ)
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={formData.birthDate.replace(/\//g, '-')}
                          onChange={(e) => {
                            const dateValue = e.target.value.replace(/-/g, '/');
                            handleInputChange('birthDate')({ target: { value: dateValue } });
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Titre du poste
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="N/A"
                          value={formData.jobTitle}
                          onChange={handleInputChange('jobTitle')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                          Nationalité
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="N/A"
                          value={formData.nationality}
                          onChange={handleInputChange('nationality')}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#007B8A',
                                boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Paper>

              {/* Address Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  mb: 3, 
                  borderRadius: 2,
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.06)'
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#1e293b' }}>
                  Adresse
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Adresse
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.address}
                      onChange={handleInputChange('address')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Ville
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.city}
                      onChange={handleInputChange('city')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Code postal
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.postalCode}
                      onChange={handleInputChange('postalCode')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Code pays
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="FR, US, CA..."
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Nom du pays
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="France, United States..."
                      value={formData.countryName}
                      onChange={handleInputChange('countryName')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      État/Province
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="State or Province"
                      value={formData.state}
                      onChange={handleInputChange('state')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Personal & Professional Details Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.06)'
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#1e293b' }}>
                  Détails personnels et professionnels
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Genre
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.gender}
                        onChange={handleInputChange('gender')}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }}
                      >
                        <MenuItem value="">Sélectionner...</MenuItem>
                        <MenuItem value="male">Homme</MenuItem>
                        <MenuItem value="female">Femme</MenuItem>
                        <MenuItem value="other">Autre</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      État civil
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.maritalStatus}
                        onChange={handleInputChange('maritalStatus')}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }}
                      >
                        <MenuItem value="">Sélectionner...</MenuItem>
                        <MenuItem value="single">Célibataire</MenuItem>
                        <MenuItem value="married">Marié(e)</MenuItem>
                        <MenuItem value="divorced">Divorcé(e)</MenuItem>
                        <MenuItem value="widowed">Veuf/Veuve</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Profession
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.profession}
                      onChange={handleInputChange('profession')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Niveau d'éducation
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.levelOfEducation}
                      onChange={handleInputChange('levelOfEducation')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Langue
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.language}
                      onChange={handleInputChange('language')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Enfants
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.kids}
                      onChange={handleInputChange('kids')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Customer & Communication Preferences Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  borderRadius: 2,
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.06)'
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#1e293b' }}>
                  Préférences client et communication
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Statut de l'abonnement
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.subscriptionStatus}
                        onChange={handleInputChange('subscriptionStatus')}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }}
                      >
                        <MenuItem value="">Sélectionner...</MenuItem>
                        <MenuItem value="subscribed">Abonné</MenuItem>
                        <MenuItem value="unsubscribed">Désabonné</MenuItem>
                        <MenuItem value="bounced">Rebondi</MenuItem>
                        <MenuItem value="pending">En attente</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Type de client
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.customerType}
                      onChange={handleInputChange('customerType')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Canal de communication préféré
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="E-mail, Téléphone, SMS..."
                      value={formData.preferredChannel}
                      onChange={handleInputChange('preferredChannel')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Forme de communication
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.formOfCommunication}
                      onChange={handleInputChange('formOfCommunication')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Niveau de satisfaction
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.levelOfSatisfaction}
                        onChange={handleInputChange('levelOfSatisfaction')}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }}
                      >
                        <MenuItem value="">Sélectionner...</MenuItem>
                        <MenuItem value="very_satisfied">Très satisfait</MenuItem>
                        <MenuItem value="satisfied">Satisfait</MenuItem>
                        <MenuItem value="neutral">Neutre</MenuItem>
                        <MenuItem value="dissatisfied">Insatisfait</MenuItem>
                        <MenuItem value="very_dissatisfied">Très insatisfait</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      ID Facebook
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="N/A"
                      value={formData.facebookId}
                      onChange={handleInputChange('facebookId')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#64748b' }}>
                      Intérêts
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Décrire les intérêts..."
                      value={formData.interests}
                      onChange={handleInputChange('interests')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#007B8A',
                            boxShadow: '0 0 0 2px rgba(0, 123, 138, 0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Container>

      {/* Fixed Footer with Action Buttons */}
      <Paper 
        elevation={4}
        sx={{ 
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          borderTop: '1px solid #dee2e6',
          backgroundColor: 'white',
          zIndex: 1000
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                color: '#1e293b',
                borderColor: '#e2e8f0',
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  borderColor: '#cbd5e1'
                }
              }}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={handleSubmit}
              disabled={saving}
              sx={{
                backgroundColor: '#007B8A',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#005D6B'
                },
                '&:disabled': {
                  backgroundColor: '#94a3b8'
                }
              }}
            >
              {saving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Création en cours...
                </>
              ) : (
                'Créer le contact'
              )}
            </Button>
          </Box>
        </Container>
      </Paper>
    </Box>
  );
};

export default ContactCreate;

